import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import { Outlet } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AppBar from '../navigation/Appbar';
import Sidebar from '../navigation/Sidebar';
import { usePatientAuth } from '../hooks/usePatientAuth';
import { TOASTIFY_NEWEST_ON_TOP } from '../../../utils/user/patient/config';
import PageThemeProvider from '../../../themes/user/patient/dashboard/theme';
import { CircularProgress, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

const DashboardLayout = () => {
  // Get patient data and add a check for the loading state
  const { patientData, isLoading: authLoading } = usePatientAuth(
    true,
    true,
    toast
  );
  const { loading: patientDataLoading } = useSelector(
    (state) => state.patientData
  );

  // Separate desktop and mobile sidebar states
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true); // For desktop (icons + text vs icons only)
  const [isSidebarVisible, setIsSidebarVisible] = useState(false); // For mobile (visible vs hidden)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notificationCount, setNotificationCount] = useState(0);

  // Add this useEffect to your DashboardLayout component
  useEffect(() => {
    // Save the original overflow style
    const originalStyle = document.body.style.overflow;

    // Set overflow to hidden
    document.body.style.overflow = 'hidden';

    // Restore original style when component unmounts
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // When switching between mobile and desktop modes
      if (mobile) {
        // On mobile, hide sidebar by default
        setIsSidebarVisible(false);
      } else {
        // On desktop, always show sidebar (expanded or collapsed)
        setIsSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle sidebar functions
  const toggleSidebarExpansion = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  const toggleSidebarVisibility = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  // Show loading indicator while fetching patient data
  if (authLoading) {
    return (
      <PageThemeProvider>
        <div className={css(styles.loadingContainer)}>
          <CircularProgress size={60} />
        </div>
      </PageThemeProvider>
    );
  }

  return (
    <PageThemeProvider>
      <div className={css(styles.layoutContainer)}>
        <AppBar
          isMobile={isMobile}
          isSidebarVisible={isSidebarVisible}
          toggleSidebarVisibility={toggleSidebarVisibility}
          notificationCount={notificationCount}
        />

        <Sidebar
          patientData={patientData}
          isSidebarExpanded={isSidebarExpanded}
          isSidebarVisible={isSidebarVisible}
          isMobile={isMobile}
          toggleSidebarExpansion={toggleSidebarExpansion}
          setIsSidebarVisible={setIsSidebarVisible}
        />

        <motion.main
          className={css(
					  styles.mainContent,
					  isMobile
					    ? styles.mainContentMobileSidebarOpen
					    : isSidebarExpanded
					      ? styles.mainContentExpandedSidebar
					      : styles.mainContentCollapsedSidebar
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Content container that centers content on large screens */}
          <div className={css(styles.contentContainer)}>
            {patientDataLoading
              ? (
                <div className={css(styles.loadingContainer)}>
                  <CircularProgress size={60} />
                </div>
                )
              : (
                <Outlet />
                )}
          </div>
        </motion.main>
        <ToastContainer newestOnTop={TOASTIFY_NEWEST_ON_TOP} />
      </div>
    </PageThemeProvider>
  );
};

const styles = StyleSheet.create({
  layoutContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    maxHeight: '100vh',
    backgroundColor: '#f6f7f9',
    overflow: 'hidden'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f6f7f9'
  },
  mainContent: {
    flexGrow: 1,
    padding: '1.5rem 1.15rem',
    transition: 'margin-left 0.3s',
    overflow: 'auto',
    height: 'calc(100vh - 64px)'
  },
  contentContainer: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    height: '100%',
    position: 'relative',
    '@media (min-width: 1600px)': {
      maxWidth: '1400px'
    },
    '@media (max-width: 768px)': {
      maxWidth: '100%'
    }
  },
  // Original sidebar positioning styles
  mainContentMobile: {
    marginLeft: 0,
    width: '100%'
  },
  mainContentMobileSidebarOpen: {
    marginLeft: 0,
    width: '100%'
  },
  mainContentExpandedSidebar: {
    marginLeft: '250px'
  },
  mainContentCollapsedSidebar: {
    marginLeft: '75px'
  }
});

export default DashboardLayout;
